import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import cooking from '../../../assets/cooking.json';
import processing from '../../../assets/processing.json';
import { Main, StyledCard, StyledCardInner } from '../styles/commonStyles';
import { Header } from './Header';

const Lottie = lazy(() => import('lottie-react'));

interface GeneratingViewProps {
	balance: number;
	totalYield: number;
	yieldProgress: number;
	aptosBalance: number;
}

interface GeneratingViewContentProps {
	balance: number;
	yieldProgress: number;
}

const ProcessingViewContent: React.FC<GeneratingViewContentProps> = () => (
	<>
		<Typography
			variant="h5"
			sx={{
				fontSize: {
					xs: '20px',
					sm: '24px',
				},
				fontWeight: 500,
				mb: 2,
				mt: 5,
			}}
			color="textPrimary"
		>
			Please Wait...
		</Typography>
		<Typography
			gutterBottom
			fontFamily="Oxanium"
			sx={{
				color: 'var(--atlas-text-muted-alt)',
				fontSize: {
					xs: '14px',
					sm: '15px',
					md: '16px',
				},
				fontWeight: 400,
			}}
		>
			We are currently processing your transaction (Tx Hash).
		</Typography>
	</>
);

const CookingViewContent: React.FC<GeneratingViewContentProps> = ({
	balance,
	yieldProgress,
}) => (
	<>
		<Typography
			variant="h5"
			sx={{
				fontSize: {
					xs: '20px',
					sm: '24px',
				},
				fontWeight: 500,
				mb: 2,
			}}
			color="textPrimary"
		>
			Cooking the, <br /> yield, sir.
		</Typography>
		<Typography
			gutterBottom
			fontFamily="Oxanium"
			sx={{
				color: 'var(--atlas-text-muted-alt)',
				fontSize: {
					xs: '14px',
					sm: '15px',
					md: '16px',
				},
				fontWeight: 400,
			}}
		>
			Your agent is actively farming yield on ${balance.toLocaleString()}{' '}
			USDC using balanced strategy
		</Typography>
		<Box mt={3}>
			<Typography
				variant="body2"
				gutterBottom
				fontFamily="Oxanium"
				sx={{
					fontSize: {
						xs: '15px',
						sm: '16px',
					},
					color: 'var(--atlas-text-muted-alt)',
				}}
				mb={2}
			>
				Yield Generation Progress: {yieldProgress}%
			</Typography>
			<LinearProgress
				variant="determinate"
				value={yieldProgress}
				sx={{
					height: '10px',
					borderRadius: '10px',
					backgroundColor: 'var(--atlas-border-light)',
				}}
			/>
		</Box>
	</>
);

export const GeneratingView: React.FC<GeneratingViewProps> = ({
	balance,
	totalYield,
	yieldProgress,
}) => {
	const [showProcessingView, setShowProcessingView] = useState(false);

	useEffect(() => {
		const timer = window.setTimeout(() => {
			setShowProcessingView(true);
		}, 1500);

		return () => window.clearTimeout(timer);
	}, []);

	const animationData = showProcessingView ? processing : cooking;

	return (
		<Main>
			<Header />
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				minHeight="60vh"
				px={{ xs: 2, sm: 0 }}
			>
				<StyledCard
					sx={{
						width: { xs: '100%', sm: '450px' },
						maxWidth: '450px',
						marginTop: { xs: '20px', sm: '30px', md: '40px' },
					}}
				>
					<StyledCardInner>
						<Box
							sx={{
								padding: {
									xs: '20px',
									sm: '24px',
									md: '28px',
								},
								textAlign: 'center',
							}}
						>
							<Box
								sx={{
									width: { xs: '150px', sm: '200px' },
									height: { xs: '150px', sm: '200px' },
									position: 'relative',
									margin: '0 auto',
								}}
							>
								<Box
									mb={2}
									sx={{
										width: {
											xs: '250px',
											sm: '350px',
										},
										height: {
											xs: '250px',
											sm: '350px',
										},
										position: 'absolute',
										top: { xs: '-30px', sm: '-40px' },
										left: {
											xs: 'calc(50% - 125px)',
											sm: 'calc(50% - 175px)',
										},
										right: 0,
										bottom: 0,
									}}
								>
									<Suspense
										fallback={
											<Box
												width="100%"
												height="100%"
												display="flex"
												alignItems="center"
												justifyContent="center"
											>
												<LinearProgress
													sx={{ width: '60%' }}
												/>
											</Box>
										}
									>
										<Lottie
											animationData={animationData}
											loop={true}
											style={{
												width: '100%',
												height: '100%',
											}}
										/>
									</Suspense>
								</Box>
							</Box>
							{showProcessingView ? (
								<ProcessingViewContent
									balance={balance}
									yieldProgress={yieldProgress}
								/>
							) : (
								<CookingViewContent
									balance={balance}
									yieldProgress={yieldProgress}
								/>
							)}
							{/*<Box
								mt={3}
								p={{ xs: 1.5, sm: 2 }}
								bgcolor="var(--atlas-accent-success-alpha-10)"
								borderRadius={1}
							>
								<Typography
									variant="h6"
									color="success.main"
									fontFamily="Oxanium"
								>
									+${totalYield.toFixed(4)} USDC
								</Typography>
								<Typography
									variant="body2"
									color="textPrimary"
									fontFamily="Oxanium"
									sx={{
										fontSize: { xs: '13px', sm: '14px' },
									}}
								>
									Yield generated so far
								</Typography>
							</Box>*/}
						</Box>
					</StyledCardInner>
				</StyledCard>
			</Box>
		</Main>
	);
};
